import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Edit } from "lucide-react";
import { cn } from "~/lib/utils";

type InlineCalendarProps = {
  propertyTitle?: string;
  onBookingComplete?: (dateTime: string) => void;
};

type ViewMode = "date" | "time";

const ALL_TIME_SLOTS = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "01:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
  "06:00 PM",
];

function startOfDay(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function isWeekendDate(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function getAvailableTimeSlotsForDate(date: Date): string[] {
  const today = startOfDay(new Date());
  const selectedDay = startOfDay(date);

  if (selectedDay.getTime() !== today.getTime()) {
    return ALL_TIME_SLOTS;
  }

  const now = new Date();
  const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);

  return ALL_TIME_SLOTS.filter((timeSlot) => {
    const [time, period] = timeSlot.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (period === "PM" && hours !== 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    const slotTime = new Date(now);
    slotTime.setHours(hours, minutes, 0, 0);

    return slotTime >= oneHourFromNow;
  });
}

function findNextDateWithSlots(fromDate: Date, maxDays = 90): Date | null {
  const candidate = startOfDay(fromDate);

  if (getAvailableTimeSlotsForDate(candidate).length === 0) {
    candidate.setDate(candidate.getDate() + 1);
  }

  for (let i = 0; i < maxDays; i++) {
    const today = startOfDay(new Date());

    if (candidate < today) {
      candidate.setDate(candidate.getDate() + 1);
      continue;
    }

    if (isWeekendDate(candidate)) {
      candidate.setDate(candidate.getDate() + 1);
      continue;
    }

    if (getAvailableTimeSlotsForDate(candidate).length > 0) {
      return new Date(candidate);
    }

    candidate.setDate(candidate.getDate() + 1);
  }

  return null;
}

function formatBookingDateTime(date: Date, time: string): string {
  const dd = date.getDate().toString().padStart(2, "0");
  const mm = date.toLocaleDateString("en-US", { month: "short" });
  const yy = date.getFullYear();
  return `${dd}-${mm}-${yy} ${time}`;
}

export function InlineCalendar({ propertyTitle, onBookingComplete }: InlineCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("date");
  const [isEditing, setIsEditing] = useState(false);

  const isDisabled = !!(selectedDate && selectedTime && !isEditing);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const resolveDateWithSlots = (date: Date): Date => {
    if (isWeekendDate(date)) {
      const next = findNextDateWithSlots(date);
      return next ?? date;
    }
    if (getAvailableTimeSlotsForDate(date).length > 0) {
      return date;
    }
    const next = findNextDateWithSlots(date);
    return next ?? date;
  };

  const applySelectedDate = (date: Date) => {
    const resolved = resolveDateWithSlots(date);
    setSelectedDate(resolved);
    setCurrentDate(new Date(resolved.getFullYear(), resolved.getMonth(), 1));
    setSelectedTime(null);
    setIsEditing(false);
    setViewMode("time");
  };

  const handleDateClick = (day: number) => {
    if (isDisabled) return;
    const selected = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    applySelectedDate(selected);
  };

  useEffect(() => {
    if (!selectedDate || viewMode !== "time" || isEditing || isDisabled) return;

    const slots = getAvailableTimeSlotsForDate(selectedDate);
    if (slots.length > 0 && !isWeekendDate(selectedDate)) return;

    const next = findNextDateWithSlots(selectedDate);
    if (next && next.getTime() !== selectedDate.getTime()) {
      setSelectedDate(next);
      setCurrentDate(new Date(next.getFullYear(), next.getMonth(), 1));
      setSelectedTime(null);
    }
  }, [selectedDate, viewMode, isEditing, isDisabled]);

  const handleTimeClick = (time: string) => {
    if (isDisabled) return;
    setSelectedTime(time);
    setIsEditing(false);

    if (selectedDate && onBookingComplete) {
      onBookingComplete(formatBookingDateTime(selectedDate, time));
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSelectedDate(null);
    setSelectedTime(null);
    setViewMode("date");
  };

  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number | null) => {
    if (!day || !selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isPastDate = (day: number | null) => {
    if (!day) return false;
    const today = startOfDay(new Date());
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return startOfDay(dateToCheck) < today;
  };

  const isWeekendDay = (day: number | null) => {
    if (!day) return false;
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return isWeekendDate(dateToCheck);
  };

  const days = getDaysInMonth(currentDate);
  const timeSlots = selectedDate ? getAvailableTimeSlotsForDate(selectedDate) : ALL_TIME_SLOTS;

  return (
    <div className="w-full max-w-full sm:max-w-[380px]">
      {viewMode === "date" ? (
        <div className="bg-[#f6f5f5] rounded-2xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={handlePreviousMonth}
              disabled={isDisabled}
              className={cn(
                "p-1 rounded transition-colors",
                isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/50",
              )}
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="text-base sm:text-lg font-medium">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>

            <button
              type="button"
              onClick={handleNextMonth}
              disabled={isDisabled}
              className={cn(
                "p-1 rounded transition-colors",
                isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/50",
              )}
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-2">
            {daysOfWeek.map((day, index) => (
              <div
                key={index}
                className="text-center text-xs sm:text-sm font-medium text-gray-600 py-1.5 sm:py-2"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {days.map((day, index) => {
              const isPast = isPastDate(day);
              const isWeekend = isWeekendDay(day);
              const dateDisabled = !day || isPast || isWeekend || isDisabled;

              return (
                <button
                  key={index}
                  type="button"
                  disabled={dateDisabled}
                  onClick={() => day && !isPast && !isWeekend && !isDisabled && handleDateClick(day)}
                  className={cn(
                    "aspect-square flex items-center justify-center rounded-full text-xs sm:text-sm transition-colors",
                    !day && "invisible",
                    (isPast || isWeekend) && "text-gray-500 cursor-not-allowed opacity-50",
                    isDisabled && "opacity-50 cursor-not-allowed",
                    day &&
                      !isPast &&
                      !isWeekend &&
                      !isDisabled &&
                      !isToday(day) &&
                      !isSelected(day) &&
                      "hover:bg-white/50 text-gray-700",
                    isToday(day) &&
                      !isSelected(day) &&
                      !isPast &&
                      !isWeekend &&
                      !isDisabled &&
                      "bg-gray-900 text-white font-semibold",
                    isSelected(day) && !isPast && !isWeekend && !isDisabled && "bg-oh-primary text-white font-semibold",
                  )}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-300">
            <button
              type="button"
              onClick={() => !isDisabled && setViewMode("date")}
              disabled={isDisabled}
              className={cn("p-2 sm:p-3 rounded-lg transition-colors bg-white", isDisabled && "opacity-50 cursor-not-allowed")}
              aria-label="Calendar view"
            >
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <button
              type="button"
              onClick={() => {
                if (selectedDate && !isDisabled) {
                  setViewMode("time");
                }
              }}
              disabled={!selectedDate || isDisabled}
              className={cn(
                "p-2 sm:p-3 rounded-lg transition-colors hover:bg-white/50",
                (!selectedDate || isDisabled) && "opacity-50 cursor-not-allowed",
              )}
              aria-label="Time view"
            >
              <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-[#f6f5f5] rounded-2xl p-4 sm:p-6">
          <div className="mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-semibold mb-1.5 sm:mb-2">Select Time</h3>
            <p className="text-xs sm:text-sm text-gray-600">
              {selectedDate?.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3 max-h-[250px] sm:max-h-[300px] overflow-y-auto">
            {timeSlots.length > 0 ? (
              timeSlots.map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => !isDisabled && handleTimeClick(time)}
                  disabled={isDisabled}
                  className={cn(
                    "px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm rounded-lg border transition-colors",
                    selectedTime === time
                      ? "bg-oh-primary text-white border-oh-primary"
                      : "border-gray-300 bg-white hover:bg-oh-primary hover:text-white hover:border-oh-primary",
                    isDisabled &&
                      "opacity-50 cursor-not-allowed hover:bg-white hover:text-gray-700 hover:border-gray-300",
                  )}
                >
                  {time}
                </button>
              ))
            ) : (
              <div className="col-span-2 text-center py-6 text-gray-600">
                <p className="text-sm">No available time slots for this day.</p>
                <p className="text-xs mt-2">Selecting the next available weekday…</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-3 sm:gap-4 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-300">
            <button
              type="button"
              onClick={() => !isDisabled && setViewMode("date")}
              disabled={isDisabled}
              className={cn(
                "p-2 sm:p-3 rounded-lg transition-colors hover:bg-white/50",
                isDisabled && "opacity-50 cursor-not-allowed",
              )}
              aria-label="Calendar view"
            >
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <button
              type="button"
              onClick={() => !isDisabled && setViewMode("time")}
              disabled={isDisabled}
              className={cn("p-2 sm:p-3 rounded-lg transition-colors bg-white", isDisabled && "opacity-50 cursor-not-allowed")}
              aria-label="Time view"
            >
              <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>
      )}

      {selectedDate && selectedTime ? (
        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-2 sm:gap-0">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-green-800 mb-1">Selected Time</p>
              <p className="text-xs text-green-700 break-words">
                {selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}{" "}
                at {selectedTime}
              </p>
            </div>
            {!isEditing && (
              <button
                type="button"
                onClick={handleEdit}
                className="sm:ml-4 flex items-center gap-1 px-2.5 sm:px-3 py-1.5 text-xs font-medium text-green-800 bg-white border border-green-300 rounded-lg hover:bg-green-100 transition-colors flex-shrink-0"
                aria-label="Edit date and time"
              >
                <Edit className="w-3 h-3" />
                Edit
              </button>
            )}
          </div>
        </div>
      ) : selectedDate && viewMode === "time" && timeSlots.length > 0 ? (
        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs sm:text-sm text-blue-800">Select a time slot to complete your booking</p>
        </div>
      ) : null}
    </div>
  );
}

